import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Minimize2, Maximize2, Send, Loader2, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Message d'accueil
      setMessages([{
        role: 'assistant',
        content: "👋 Bonjour ! Je suis votre copilote IA Baobab Connect.\n\nJe peux vous aider à :\n• Recommander des chauffeurs\n• Proposer des tarifs\n• Analyser les performances\n• Répondre à vos questions\n\nComment puis-je vous aider ?",
        timestamp: new Date().toISOString()
      }]);
      
      // Générer un nouveau session_id
      setSessionId(`copilot-widget-${Date.now()}`);
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Ajouter le message de l'utilisateur
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    setIsLoading(true);

    try {
      const response = await api.post('/ai/copilot/chat', {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.response,
          timestamp: response.data.timestamp
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Erreur copilote:', error);
      toast.error('Erreur lors de la communication avec l\'IA');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolé, une erreur s'est produite. Veuillez réessayer.",
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-200 group"
        aria-label="Ouvrir le copilote IA"
      >
        <Bot className="h-6 w-6 sm:h-7 sm:w-7" />
        <span className="absolute -top-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500"></span>
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 transition-all duration-300 ${
        isMinimized 
          ? 'bottom-4 right-4 sm:bottom-6 sm:right-6 w-72 sm:w-80 h-14 sm:h-16' 
          : 'bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 h-[calc(100vh-4rem)] sm:h-[600px] max-w-md'
      }`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-3 sm:p-4 rounded-t-2xl flex items-center justify-between cursor-pointer"
           onClick={() => setIsMinimized(!isMinimized)}>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
            <Bot className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <div>
            <h3 className="font-bold text-xs sm:text-sm">Copilote IA</h3>
            {!isMinimized && <p className="text-[10px] sm:text-xs text-emerald-100">Assistant intelligent</p>}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(!isMinimized);
            }}
            className="hover:bg-white/20 p-1.5 rounded transition-colors"
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            className="hover:bg-white/20 p-1.5 rounded transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 sm:space-y-3 h-[calc(100%-180px)] sm:h-[460px] bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex space-x-2 max-w-[85%] ${
                    message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
                      message.role === 'user'
                        ? 'bg-emerald-600'
                        : message.isError
                        ? 'bg-red-500'
                        : 'bg-gradient-to-br from-emerald-500 to-emerald-700'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <span className="text-xs font-bold text-white">U</span>
                    ) : (
                      <Sparkles className="h-4 w-4 text-white" />
                    )}
                  </div>

                  {/* Message */}
                  <div
                    className={`rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 ${
                      message.role === 'user'
                        ? 'bg-emerald-600 text-white'
                        : message.isError
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-white text-gray-800 shadow-sm border border-gray-200'
                    }`}
                  >
                    <p className="text-xs whitespace-pre-wrap leading-relaxed">{message.content}</p>
                    {message.timestamp && (
                      <p
                        className={`text-[10px] mt-1 ${
                          message.role === 'user' ? 'text-blue-100' : 'text-slate-400'
                        }`}
                      >
                        {formatTime(message.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="flex space-x-2 max-w-[85%]">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-white rounded-xl px-3 py-2 shadow-sm border border-slate-200">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="h-3 w-3 animate-spin text-slate-600" />
                      <p className="text-xs text-slate-600">L'IA réfléchit...</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-2 sm:p-3 bg-white rounded-b-2xl">
            <form onSubmit={sendMessage} className="flex space-x-1.5 sm:space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Posez votre question..."
                disabled={isLoading}
                className="flex-1 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100"
              />
              <button
                type="submit"
                disabled={isLoading || !inputMessage.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white p-1.5 sm:p-2 rounded-lg transition-colors disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </form>

            {/* Quick suggestions */}
            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1">
              <button
                onClick={() => setInputMessage('Meilleurs chauffeurs ?')}
                disabled={isLoading}
                className="text-[10px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2 py-0.5 sm:py-1 rounded-full transition-colors border border-emerald-200"
              >
                Top chauffeurs
              </button>
              <button
                onClick={() => setInputMessage('Tarif Abidjan-Bouaké ?')}
                disabled={isLoading}
                className="text-[10px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2 py-0.5 sm:py-1 rounded-full transition-colors border border-emerald-200"
              >
                Tarifs
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FloatingChatWidget;
